import {i18n} from '@lingui/core'

import type {CurrencyType} from '@pleo-io/deimos'
import {CURRENCIES} from '@pleo-io/deimos'

import type {Brand} from 'types/utility'
import {invariant} from '~/error/invariant'

type FormatCurrencyOptions = {
    showPlus?: boolean
    format?: Intl.NumberFormatOptions
}

/**
 * Function for rendering currencies using the i18n library, provides a fallback if no number is provided for loading states.
 * @param amount number, to be formatted
 * @param currency string, The 3-letter ISO 4217 code of the currency that the amount will be formatted in (e.g. 'DKK')
 * @param options object, additional options to allow for further formatting of currency
 * @returns Formatted string, formatted currency using format provided, or - if no amount provided.
 * @example.
 * const currencyFormat = getCurrencyFormat()
 * const formattedCurrency = formatCurrency(10000, currencyFormat)
 */

export const formatCurrency = (
    amount: number | null | undefined,
    currency?: CurrencyType | string | null,
    options?: FormatCurrencyOptions,
): string => {
    if (!amount && amount !== 0) {
        return '-'
    }

    const shouldShowPlus = options?.showPlus && amount > 0
    const currencyFormat = getCurrencyFormat(currency, {
        ...options?.format,
        ...(shouldShowPlus && {
            signDisplay: 'always',
        }),
    })

    // eslint-disable-next-line string-to-lingui/forbid-i18n-calls
    const formattedValue = i18n.number(amount, currencyFormat)

    if (currencyFormat.currencyDisplay === 'code' && currency) {
        return appendCurrencyCode(formattedValue, currency)
    }

    return formattedValue
}

export const appendCurrencyCode = (formattedValue: string, currencyCode: CurrencyType | string) => {
    if (!formattedValue.includes(currencyCode)) {
        return ''
    }
    const value = formattedValue
        .split(currencyCode)
        .join('')
        .trim()
        .replace(/([+-])\s+/g, '$1')
    return `${value} ${currencyCode}`
}

export const formatCurrencyParts = (
    amount: number | null | undefined,
    currency?: CurrencyType | string | null,
    options?: FormatCurrencyOptions,
) => {
    const formattedCurrency = formatCurrency(amount, currency, options)
    const [value, code] = formattedCurrency.split(' ')

    return {
        value: value ?? '',
        currency: code ?? '',
    }
}

export const CurrencyFormat = {
    FixedFractionDigits(digit = 2) {
        return {
            minimumFractionDigits: digit,
            maximumFractionDigits: digit,
        }
    },
}

export const isCurrency = (currency: string): currency is CurrencyType =>
    CURRENCIES.includes(currency as CurrencyType)

export const getCurrencyFormat = (
    currency?: CurrencyType | string | null | undefined,
    options?: Intl.NumberFormatOptions,
): Intl.NumberFormatOptions => ({
    style: 'decimal',
    ...(currency && {
        style: 'currency',
        currency,
        currencyDisplay: 'code',
    }),
    ...options,
})

export type MajorCurrency = Brand<number, 'MajorCurrency'>

export type MinorCurrency = Brand<number, 'MinorCurrency'>

type Currency = MajorCurrency | MinorCurrency

const isValidCurrency = (value: number): value is Currency => {
    return Number.isInteger(value)
}

/**
 * Function to format minor currencies (e.g. not decimalised integer values) to localised currency format
 * @param options.value The monetary value as a minor currency (must be an integer)
 * @param options.currency The ISO currency code
 * @returns Localised formatted currency and code (e.g. 4.99 EUR)
 * @example
 * const formattedCurrency = formatMinorCurrency({value: 10000, currency: 'EUR'}) // 100.00 EUR
 */
export const formatMinorCurrency = ({
    value,
    currency,
    displaySign,
}: {
    value: MinorCurrency | null | undefined
    currency: string
    displaySign?: boolean
}) => {
    if (!value && value !== 0) {
        return '-'
    }

    const {formattedValue, literal, currencyCode} = formatMinorCurrencyParts({
        value,
        currency,
        displaySign,
    })

    // We are moving the currency code to the end of the string for now to
    // be consistent with previously formatted currencies. This could be
    // passed in as an option in the future.
    const formattedCurrency = `${formattedValue}${literal}${currencyCode}`

    return formattedCurrency
}

export const formatMinorCurrencyParts = ({
    value,
    currency,
    displaySign,
}: {
    value: MinorCurrency
    currency: string
    displaySign?: boolean
}) => {
    invariant(isValidCurrency(value), 'Value must be currency in minors')

    const formatter = Intl.NumberFormat(i18n.locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'code',
        ...(displaySign && {
            signDisplay: 'exceptZero',
        }),
    })

    const options = formatter.resolvedOptions()

    const formattedToParts = formatter.formatToParts(value / 10 ** options.maximumFractionDigits)

    let literal = ''
    let currencyCode = ''

    const formattedValue = formattedToParts.reduce((accumulator, nextValue) => {
        if (nextValue.type === 'literal') {
            literal = nextValue.value
            return accumulator
        }

        if (nextValue.type === 'currency') {
            currencyCode = nextValue.value
            return accumulator
        }

        return `${accumulator}${nextValue.value}`
    }, '')

    return {
        formattedValue,
        literal,
        currencyCode,
    }
}

export function convertToMajorCurrency({
    value,
    currency,
}: {
    value: MinorCurrency
    currency: string
}) {
    invariant(isValidCurrency(value), 'Value must be currency in minors')

    const formatter = Intl.NumberFormat(i18n.locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
    })

    const options = formatter.resolvedOptions()

    const majorValue = Math.round(value / 10 ** options.maximumFractionDigits)
    return majorValue
}

export function convertToMinorCurrency({value, currency}: {value: number; currency: string}) {
    const formatter = Intl.NumberFormat(i18n.locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
    })

    const options = formatter.resolvedOptions()

    const minorValue = Math.round(value * 10 ** options.maximumFractionDigits)
    return minorValue
}
